import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCw, LayoutGrid, Table as TableIcon, Activity, AlertTriangle, Ban, ArrowUpDown, MapPin, AlertOctagon } from "lucide-react";
import { getPresetCoords, getPresetGroups } from "@/lib/formationPresets";
import { isStaff, isAdminLike, userCategories } from "@/lib/roles";
import { PlaysBar, PlayDesignerToolbar, RoutesOverlay, usePlayAnimation } from "@/components/plays/Plays";

const CATS = [
  { key: "tackle", label: "Tackle", units: ["offense", "defense", "special"] },
  { key: "flag_masculino", label: "Flag Masculino", units: ["offense", "defense"] },
  { key: "flag_femenino", label: "Flag Femenino", units: ["offense", "defense"] },
];

const UNIT_LABEL = { offense: "Ofensiva", defense: "Defensiva", special: "Equipos Especiales" };
const SLOT_LABEL = { starter: "Titular", backup: "Suplente", reserve: "Reserva" };
const SLOT_COLOR = { starter: "border-[#34C759] text-[#34C759]", backup: "border-[#007AFF] text-[#007AFF]", reserve: "border-[#71717A] text-[#A1A1AA]" };
const STATUS_BADGE = {
  active: { label: "Activo", cls: "text-[#A1A1AA] border-[#27272A]" },
  injured: { label: "Lesionado", cls: "text-[#FF9F0A] border-[#FF9F0A]" },
  suspended: { label: "Suspendido", cls: "text-[#FF3B30] border-[#FF3B30]" },
};

export default function DepthChart() {
  const { user, profile } = useAuth();
  const canEdit = isStaff(user?.role);
  // Filter visible categories by what the user has access to
  const allowedCats = useMemo(() => {
    if (isAdminLike(user?.role)) return CATS;
    const allowed = userCategories(user, profile);
    return CATS.filter(c => allowed.has(c.key));
  }, [user, profile]);
  const [category, setCategory] = useState(() => allowedCats[0]?.key || "tackle");
  const [unit, setUnit] = useState("offense");
  const [formatType, setFormatType] = useState(() => {
    try { return sessionStorage.getItem("fbclub_depth_format") || "11v11"; } catch { return "11v11"; }
  });
  const [view, setView] = useState("formation"); // formation | depth | table
  const [slots, setSlots] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);

  const units = CATS.find(c => c.key === category)?.units || ["offense"];
  // 9v9 only applies to tackle. We expose an "effective" format that auto-coerces
  // to 11v11 whenever the active category isn't tackle, so any API call uses a
  // valid combination even if the toggle state still says "9v9" from a previous
  // tackle session (the toggle UI is also hidden in that case).
  const effectiveFormat = category === "tackle" ? formatType : "11v11";
  useEffect(() => {
    if (category !== "tackle" && formatType !== "11v11") setFormatType("11v11");
  }, [category, formatType]);
  useEffect(() => {
    try { sessionStorage.setItem("fbclub_depth_format", formatType); }
    catch (err) { console.warn("depth: sessionStorage write failed", err?.message); }
  }, [formatType]);

  // Ensure selected category is one the user can access; otherwise switch to first allowed
  useEffect(() => {
    if (allowedCats.length === 0) return;
    if (!allowedCats.find(c => c.key === category)) {
      setCategory(allowedCats[0].key);
    }
  }, [allowedCats, category]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get(`/depth?category=${category}&unit=${unit}&format_type=${effectiveFormat}`),
        canEdit ? api.get(`/players?category=${category}`) : Promise.resolve({ data: [] }),
      ]);
      setSlots(s.data);
      setPlayers(p.data);
    } finally { setLoading(false); }
  }, [category, unit, effectiveFormat, canEdit]);

  useEffect(() => { load(); }, [load]);

  // Allow children (FormationView) to trigger reload after applying a formation
  useEffect(() => {
    const fn = () => load();
    window.addEventListener("depth-reload", fn);
    return () => window.removeEventListener("depth-reload", fn);
  }, [load]);

  // Ensure unit valid when category changes
  useEffect(() => {
    if (!units.includes(unit)) setUnit(units[0]);
  }, [units, unit]);

  const playerMap = useMemo(() => Object.fromEntries(players.map(p => [p.user_id, p])), [players]);
  const assignedIds = useMemo(() => new Set(slots.filter(s => s.player_id).map(s => s.player_id)), [slots]);

  const seed = async () => {
    if (!confirm("Crear posiciones por defecto para esta unidad?")) return;
    await api.post(`/depth/seed?category=${category}&unit=${unit}&format_type=${effectiveFormat}`);
    toast.success("Posiciones cargadas");
    load();
  };

  const clearUnit = async () => {
    if (!confirm("¿Borrar TODO el depth chart de esta unidad?")) return;
    await api.post(`/depth/clear?category=${category}&unit=${unit}&format_type=${effectiveFormat}`);
    toast.success("Limpiado");
    load();
  };

  const assignToSlot = async (slotId, playerId) => {
    // optimistic: remove player from any existing slot first, then assign
    const prev = [...slots];
    const existing = prev.find(s => s.player_id === playerId);
    const updated = prev.map(s => {
      if (s.id === slotId) return { ...s, player_id: playerId };
      if (existing && s.id === existing.id) return { ...s, player_id: null };
      return s;
    });
    setSlots(updated);
    try {
      if (existing && existing.id !== slotId) {
        await api.patch(`/depth/${existing.id}`, { player_id: null });
      }
      await api.patch(`/depth/${slotId}`, { player_id: playerId });
    } catch {
      toast.error("Error al asignar");
      load();
    }
  };

  const clearSlot = async (slotId) => {
    setSlots(prev => prev.map(s => s.id === slotId ? { ...s, player_id: null } : s));
    try { await api.patch(`/depth/${slotId}`, { player_id: null }); }
    catch { load(); }
  };

  const removeSlot = async (slotId) => {
    if (!confirm("¿Eliminar este slot?")) return;
    await api.delete(`/depth/${slotId}`);
    load();
  };

  const addPosition = async () => {
    const pos = prompt("Nombre de la posición (ej: WR3):");
    if (!pos) return;
    for (const slot of ["starter", "backup", "reserve"]) {
      await api.post("/depth", { category, unit, position: pos, slot, format_type: effectiveFormat, order: 0, player_id: null });
    }
    load();
  };

  // Drag and drop handlers (HTML5)
  const onDragStart = (e, payload) => {
    e.dataTransfer.setData("text/plain", JSON.stringify(payload));
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDropOnSlot = (e, slotId) => {
    e.preventDefault();
    try {
      const payload = JSON.parse(e.dataTransfer.getData("text/plain"));
      if (payload.player_id) assignToSlot(slotId, payload.player_id);
    } catch (err) {
      // External drag (file, browser link, etc.) → ignore but log.
      console.warn("depth: invalid drop payload on slot", err?.message);
    }
  };
  const onDropOnPool = (e) => {
    e.preventDefault();
    try {
      const payload = JSON.parse(e.dataTransfer.getData("text/plain"));
      if (payload.from_slot) clearSlot(payload.from_slot);
    } catch (err) {
      console.warn("depth: invalid drop payload on pool", err?.message);
    }
  };

  // Group slots by position for field/table views
  const byPosition = useMemo(() => {
    const m = {};
    slots.forEach(s => { (m[s.position] ||= []).push(s); });
    Object.values(m).forEach(arr => arr.sort((a, b) => {
      const order = { starter: 0, backup: 1, reserve: 2 };
      return order[a.slot] - order[b.slot] || a.order - b.order;
    }));
    return m;
  }, [slots]);

  const availablePlayers = useMemo(() => players.filter(p => !assignedIds.has(p.user_id)), [players, assignedIds]);

  // Auto-promotion suggestions: titular con status != active y con backup activo disponible
  const promotionAlerts = useMemo(() => {
    const out = [];
    Object.values(byPosition).forEach(arr => {
      const starter = arr.find(s => s.slot === "starter");
      const backup = arr.find(s => s.slot === "backup");
      if (!starter || !starter.player_id) return;
      const stPlayer = playerMap[starter.player_id];
      if (!stPlayer) return;
      const stStatus = stPlayer.profile?.status || "active";
      if (stStatus === "active") return;
      // backup with active player
      const bkPlayer = backup?.player_id ? playerMap[backup.player_id] : null;
      const bkStatus = bkPlayer?.profile?.status || "active";
      out.push({
        position: starter.position,
        starterSlot: starter,
        backupSlot: backup,
        starter: stPlayer,
        backup: bkPlayer,
        starterStatus: stStatus,
        backupAvailable: !!bkPlayer && bkStatus === "active",
      });
    });
    return out;
  }, [byPosition, playerMap]);

  const promoteBackup = async (starterSlotId, backupSlotId) => {
    if (!backupSlotId) return;
    await api.post("/depth/swap", { slot_a: starterSlotId, slot_b: backupSlotId });
    toast.success("Suplente promovido a titular");
    load();
  };

  const applyPresetCoords = async (coordsByPosition) => {
    // Map each starter slot (by position) to new coords + PATCH in parallel
    const updates = [];
    const optimistic = slots.map(s => {
      if (s.slot !== "starter") return s;
      const c = coordsByPosition[s.position];
      if (!c) return s;
      updates.push({ id: s.id, x: c[0], y: c[1] });
      return { ...s, x: c[0], y: c[1] };
    });
    setSlots(optimistic);
    try {
      await Promise.all(updates.map(u => api.patch(`/depth/${u.id}`, { x: u.x, y: u.y })));
    } catch {
      toast.error("Error aplicando preset");
      load();
    }
  };

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1800px]">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="overline">Tactics</div>
            <h1 className="font-display text-4xl sm:text-5xl text-white leading-none">DEPTH CHART</h1>
            <p className="text-[#A1A1AA] mt-2 text-sm">Distribución del roster por unidad, depth chart y posiciones.</p>
          </div>
          <div className="flex gap-1 border border-[#27272A]">
            {[
              { k: "formation", label: "Cancha", Icon: MapPin },
              { k: "depth", label: "Depth", Icon: LayoutGrid },
              { k: "table", label: "Tabla", Icon: TableIcon },
            ].map(v => (
              <button
                key={v.k}
                onClick={() => setView(v.k)}
                data-testid={`view-${v.k}`}
                className={`px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 ${view === v.k ? "bg-[#1A1A1A] text-white" : "text-[#A1A1AA] hover:text-white"}`}
              >
                <v.Icon size={12}/> {v.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            {canEdit && (
              <>
                <button onClick={addPosition} className="btn-ghost text-xs flex items-center gap-2" data-testid="add-position-btn">
                  <Plus size={14}/> Posición
                </button>
                <button onClick={seed} className="btn-ghost text-xs flex items-center gap-2" data-testid="seed-btn">
                  <RefreshCw size={14}/> Cargar por defecto
                </button>
                <button onClick={clearUnit} className="btn-ghost text-xs flex items-center gap-2 text-[#FF3B30]" data-testid="clear-btn">
                  <Trash2 size={14}/> Limpiar
                </button>
              </>
            )}
          </div>
        </div>

        {/* Category tabs — limited to user's effective categories */}
        {allowedCats.length === 0 ? (
          <div className="tactical-card p-8 mb-6 text-center text-[#A1A1AA]" data-testid="depth-no-cats">
            No tienes categorías asignadas. Solicita al Admin que te asigne una rama.
          </div>
        ) : (
          <div className={`grid gap-0 border border-[#27272A] mb-2`} style={{ gridTemplateColumns: `repeat(${allowedCats.length}, minmax(0, 1fr))` }}>
            {allowedCats.map(c => (
              <button
                key={c.key}
                onClick={() => setCategory(c.key)}
                data-testid={`cat-tab-${c.key}`}
                className={`p-4 text-left border-r border-[#27272A] last:border-r-0 transition-colors ${category === c.key ? "bg-[#1A1A1A]" : "hover:bg-[#121212]"}`}
              >
                <div className={`overline ${category === c.key ? "text-[#007AFF]" : "text-[#71717A]"}`}>{c.label}</div>
                <div className={`font-display text-2xl mt-1 ${category === c.key ? "text-white" : "text-[#A1A1AA]"}`}>{c.label.split(" ")[0].toUpperCase()}</div>
              </button>
            ))}
          </div>
        )}

        {/* Unit tabs + Format toggle (Tackle only) */}
        <div className="flex border border-t-0 border-[#27272A] mb-6 flex-wrap items-stretch">
          <div className="flex flex-1 min-w-0">
            {units.map(u => (
              <button
                key={u}
                onClick={() => setUnit(u)}
                data-testid={`unit-tab-${u}`}
                className={`px-5 py-3 text-xs uppercase tracking-wider font-bold border-r border-[#27272A] last:border-r-0 transition-colors ${unit === u ? "text-white bg-[#1A1A1A] border-b-2 border-b-[#007AFF]" : "text-[#A1A1AA] hover:text-white"}`}
              >
                {UNIT_LABEL[u]}
              </button>
            ))}
          </div>
          {category === "tackle" && (
            <div className="flex border-l border-[#27272A]" data-testid="format-toggle">
              <div className="px-3 py-3 text-[10px] uppercase tracking-widest font-bold text-[#71717A] border-r border-[#27272A] bg-[#0F0F0F]">
                Formato
              </div>
              {["11v11", "9v9"].map(f => (
                <button
                  key={f}
                  onClick={() => setFormatType(f)}
                  data-testid={`format-tab-${f}`}
                  className={`px-4 py-3 text-xs uppercase tracking-wider font-bold border-r border-[#27272A] last:border-r-0 transition-colors ${formatType === f ? "text-white bg-[#1A1A1A] border-b-2 border-b-[#FF9F0A]" : "text-[#A1A1AA] hover:text-white"}`}
                >
                  {f}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-[#71717A] text-sm">Cargando...</div>
        ) : slots.length === 0 ? (
          <div className="tactical-card p-10 text-center">
            <div className="text-[#A1A1AA] mb-3">No hay posiciones cargadas para esta unidad.</div>
            {canEdit && <button onClick={seed} className="btn-primary text-xs">CARGAR POSICIONES POR DEFECTO</button>}
          </div>
        ) : (
          <>
            {/* Auto-promotion alerts */}
            {canEdit && promotionAlerts.length > 0 && (
              <div className="tactical-card p-4 mb-4" style={{ borderColor: "#FF9F0A", background: "linear-gradient(90deg, rgba(255,159,10,0.08) 0%, transparent 60%)" }} data-testid="promotion-alerts">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle size={16} className="text-[#FF9F0A]"/>
                  <div className="overline text-[#FF9F0A]">Alineación comprometida</div>
                </div>
                <div className="space-y-2">
                  {promotionAlerts.map(a => (
                    <div key={a.starterSlot.id} className="flex items-center justify-between gap-3 flex-wrap" data-testid={`promote-row-${a.position}`}>
                      <div className="text-sm flex-1 min-w-0">
                        <span className="font-display text-lg text-white mr-2">{a.position}</span>
                        <span className="text-[#A1A1AA]">titular </span>
                        <span className="text-white font-bold">{a.starter.name}</span>
                        <span className={`ml-2 text-[10px] uppercase px-1.5 py-0.5 border ${STATUS_BADGE[a.starterStatus].cls}`}>{STATUS_BADGE[a.starterStatus].label}</span>
                        {a.backupAvailable ? (
                          <span className="text-[#A1A1AA] ml-2">— suplente <span className="text-white font-bold">{a.backup.name}</span> listo</span>
                        ) : a.backup ? (
                          <span className="text-[#71717A] ml-2">— suplente {a.backup.name} también no disponible</span>
                        ) : (
                          <span className="text-[#71717A] ml-2">— sin suplente asignado</span>
                        )}
                      </div>
                      {a.backupAvailable && (
                        <button
                          onClick={() => promoteBackup(a.starterSlot.id, a.backupSlot.id)}
                          className="btn-primary text-xs flex items-center gap-1.5"
                          data-testid={`promote-btn-${a.position}`}
                        >
                          <ArrowUpDown size={12}/> Promover
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Players pool (left) */}
            {canEdit && (
              <div
                className="tactical-card p-4 lg:col-span-1"
                onDragOver={onDragOver}
                onDrop={onDropOnPool}
                data-testid="players-pool"
              >
                <div className="overline mb-3">Jugadores disponibles ({availablePlayers.length})</div>
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                  {availablePlayers.length === 0 && <div className="text-[#71717A] text-xs">Todos asignados.</div>}
                  {availablePlayers.map(p => (
                    <PlayerChip key={p.user_id} player={p} draggable onDragStart={(e) => onDragStart(e, { player_id: p.user_id })}/>
                  ))}
                </div>
              </div>
            )}

            {/* View renderer */}
            <div className={canEdit ? "lg:col-span-3" : "lg:col-span-4"}>
              {view === "formation" ? (
                <FormationView
                  slots={slots}
                  byPosition={byPosition}
                  playerMap={playerMap}
                  unit={unit}
                  category={category}
                  formatType={effectiveFormat}
                  canEdit={canEdit}
                  onDragStart={onDragStart}
                  onDragOver={onDragOver}
                  onDropOnSlot={onDropOnSlot}
                  onClear={clearSlot}
                  onApplyPresetCoords={applyPresetCoords}
                  onPositionMove={async (slotId, x, y) => {
                    setSlots(prev => prev.map(s => s.id === slotId ? { ...s, x, y } : s));
                    try { await api.patch(`/depth/${slotId}`, { x, y }); }
                    catch { load(); }
                  }}
                  onSwap={async (a, b) => {
                    await api.post("/depth/swap", { slot_a: a, slot_b: b });
                    toast.success("Suplente promovido a titular");
                    load();
                  }}
                />
              ) : view === "depth" ? (
                <FieldView
                  byPosition={byPosition}
                  playerMap={playerMap}
                  unit={unit}
                  canEdit={canEdit}
                  onDragStart={onDragStart}
                  onDragOver={onDragOver}
                  onDropOnSlot={onDropOnSlot}
                  onClear={clearSlot}
                  onRemove={removeSlot}
                />
              ) : (
                <TableView byPosition={byPosition} playerMap={playerMap}/>
              )}
            </div>
            </div>
          </>
        )}

        <div className="h-20 md:h-6"/>
      </div>
    </Layout>
  );
}

function PlayerChip({ player, draggable, onDragStart, dim }) {
  const status = player?.profile?.status || "active";
  const stBadge = STATUS_BADGE[status] || STATUS_BADGE.active;
  const moroso = !!player?.delinquency?.is_delinquent;
  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      className={`bg-[#0A0A0A] border p-2.5 flex items-center gap-2 cursor-grab active:cursor-grabbing hover:border-[#007AFF] ${dim ? "opacity-60" : ""} ${moroso ? "border-[#FF3B30]" : "border-[#27272A]"}`}
      data-testid={`chip-${player.user_id}`}
    >
      <div className="w-8 h-8 bg-[#1A1A1A] border border-[#27272A] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
        {player.profile?.jersey_number ?? player.name?.[0] ?? "?"}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-bold text-white truncate uppercase tracking-wide">{player.name}</div>
        <div className="flex items-center gap-1 flex-wrap mt-0.5">
          {player.profile?.position && <span className="text-[9px] text-[#71717A] uppercase">{player.profile.position}</span>}
          {status !== "active" && (
            <span className={`text-[9px] px-1.5 py-px border ${stBadge.cls} uppercase tracking-wider`}>{stBadge.label}</span>
          )}
          {moroso && (
            <span
              className="text-[9px] px-1.5 py-px border border-[#FF3B30] text-[#FF3B30] uppercase tracking-wider flex items-center gap-1"
              title={`Adeuda ${player.delinquency.months_overdue} cuotas`}
              data-testid={`chip-moroso-${player.user_id}`}
            >
              <AlertOctagon size={9}/> Moroso
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function SlotCell({ slot, player, canEdit, onDragStart, onDragOver, onDropOnSlot, onClear }) {
  const empty = !player;
  return (
    <div
      onDragOver={canEdit ? onDragOver : undefined}
      onDrop={canEdit ? (e) => onDropOnSlot(e, slot.id) : undefined}
      className={`border ${SLOT_COLOR[slot.slot]} bg-[#0A0A0A] p-2 min-h-[64px] flex items-center gap-2 ${empty ? "border-dashed" : ""}`}
      data-testid={`slot-${slot.id}`}
    >
      <span className={`text-[9px] font-bold uppercase tracking-wider ${SLOT_COLOR[slot.slot].split(" ")[1]}`}>
        {SLOT_LABEL[slot.slot][0]}
      </span>
      {empty ? (
        <span className="text-[#71717A] text-xs italic">— vacío —</span>
      ) : (
        <>
          <div
            draggable={canEdit}
            onDragStart={canEdit ? (e) => onDragStart(e, { player_id: player.user_id, from_slot: slot.id }) : undefined}
            className={`flex items-center gap-2 flex-1 min-w-0 ${canEdit ? "cursor-grab active:cursor-grabbing" : ""}`}
          >
            <div className="w-7 h-7 bg-[#1A1A1A] border border-[#27272A] flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
              {player.profile?.jersey_number ?? player.name?.[0]}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold text-white truncate">{player.name}</div>
              <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                {(player.profile?.status && player.profile.status !== "active") && (
                  <span className={`text-[9px] px-1 border ${STATUS_BADGE[player.profile.status].cls}`}>{STATUS_BADGE[player.profile.status].label}</span>
                )}
                {player.delinquency?.is_delinquent && (
                  <span
                    className="text-[9px] px-1 border border-[#FF3B30] text-[#FF3B30] uppercase tracking-wider flex items-center gap-1"
                    title={`Adeuda ${player.delinquency.months_overdue} cuotas`}
                    data-testid={`slot-moroso-${player.user_id}`}
                  >
                    <AlertOctagon size={8}/> Moroso
                  </span>
                )}
              </div>
            </div>
          </div>
          {canEdit && (
            <button onClick={() => onClear(slot.id)} className="text-[#71717A] hover:text-[#FF3B30] flex-shrink-0" title="Quitar">
              <Trash2 size={12}/>
            </button>
          )}
        </>
      )}
    </div>
  );
}

function FieldView({ byPosition, playerMap, unit, canEdit, onDragStart, onDragOver, onDropOnSlot, onClear, onRemove }) {
  const positions = Object.keys(byPosition);
  return (
    <div className="space-y-6">
      {/* Field visualization */}
      <div className="relative tactical-card overflow-hidden" style={{
        backgroundImage: "repeating-linear-gradient(90deg, transparent, transparent 9.9%, rgba(255,255,255,0.05) 10%), linear-gradient(180deg, #0d2818 0%, #0a1e12 100%)"
      }}>
        <div className="absolute top-3 left-4 right-4 flex items-center justify-between">
          <span className="overline text-[#34C759]">{UNIT_LABEL[unit]}</span>
          <span className="overline text-[#A1A1AA]">TITULARES EN CANCHA</span>
        </div>
        <div className="pt-12 pb-6 px-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {positions.map(pos => {
            const starter = byPosition[pos].find(s => s.slot === "starter");
            const player = starter?.player_id ? playerMap[starter.player_id] : null;
            return (
              <div
                key={pos}
                onDragOver={canEdit && starter ? onDragOver : undefined}
                onDrop={canEdit && starter ? (e) => onDropOnSlot(e, starter.id) : undefined}
                className="bg-black/50 backdrop-blur-sm border border-[#34C759]/40 p-3 text-center hover:border-[#34C759]"
                data-testid={`field-pos-${pos}`}
              >
                <div className="font-display text-lg text-[#34C759]">{pos}</div>
                <div className="text-xs text-white mt-1 truncate font-bold">
                  {player?.name?.toUpperCase() || <span className="text-[#71717A] italic">vacío</span>}
                </div>
                {player?.profile?.jersey_number && <div className="text-[10px] text-[#A1A1AA]">#{player.profile.jersey_number}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Depth chart by position */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {positions.map(pos => (
          <div key={pos} className="tactical-card p-4" data-testid={`pos-block-${pos}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-xl text-white">{pos}</div>
              <span className="overline text-[#71717A]">{byPosition[pos].length} slots</span>
            </div>
            <div className="space-y-2">
              {byPosition[pos].map(s => (
                <div key={s.id} className="flex items-center gap-2">
                  <div className="flex-1">
                    <SlotCell
                      slot={s}
                      player={s.player_id ? playerMap[s.player_id] : null}
                      canEdit={canEdit}
                      onDragStart={onDragStart}
                      onDragOver={onDragOver}
                      onDropOnSlot={onDropOnSlot}
                      onClear={onClear}
                    />
                  </div>
                  {canEdit && (
                    <button onClick={() => onRemove(s.id)} className="text-[#71717A] hover:text-[#FF3B30] p-1" title="Eliminar slot">
                      <Trash2 size={12}/>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TableView({ byPosition, playerMap }) {
  const positions = Object.keys(byPosition);
  const getSlot = (pos, slotName) => {
    const s = byPosition[pos].find(x => x.slot === slotName);
    if (!s?.player_id) return null;
    return playerMap[s.player_id];
  };
  return (
    <div className="tactical-card overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#27272A]">
            <th className="text-left p-4 overline">Posición</th>
            <th className="text-left p-4 overline text-[#34C759]">Titular</th>
            <th className="text-left p-4 overline text-[#007AFF]">Suplente</th>
            <th className="text-left p-4 overline text-[#A1A1AA]">Reserva</th>
          </tr>
        </thead>
        <tbody>
          {positions.map(pos => {
            const starter = getSlot(pos, "starter");
            const backup = getSlot(pos, "backup");
            const reserve = getSlot(pos, "reserve");
            return (
              <tr key={pos} className="border-b border-[#27272A] hover:bg-[#121212]" data-testid={`row-${pos}`}>
                <td className="p-4 font-display text-lg text-white">{pos}</td>
                <td className="p-4 text-white">{starter ? <PlayerInline p={starter}/> : <span className="text-[#71717A] text-xs italic">—</span>}</td>
                <td className="p-4 text-white">{backup ? <PlayerInline p={backup}/> : <span className="text-[#71717A] text-xs italic">—</span>}</td>
                <td className="p-4 text-white">{reserve ? <PlayerInline p={reserve}/> : <span className="text-[#71717A] text-xs italic">—</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PlayerInline({ p }) {
  const status = p.profile?.status || "active";
  const Icon = status === "injured" ? AlertTriangle : status === "suspended" ? Ban : Activity;
  const color = status === "injured" ? "#FF9F0A" : status === "suspended" ? "#FF3B30" : "#71717A";
  const moroso = !!p.delinquency?.is_delinquent;
  return (
    <span className="flex items-center gap-2">
      {p.profile?.jersey_number && <span className="text-xs font-mono text-[#71717A]">#{p.profile.jersey_number}</span>}
      <span>{p.name}</span>
      <Icon size={12} style={{ color }}/>
      {moroso && (
        <AlertOctagon
          size={12}
          className="text-[#FF3B30]"
          aria-label="Moroso"
          data-testid={`inline-moroso-${p.user_id}`}
        />
      )}
    </span>
  );
}


function FormationView({ slots, byPosition, playerMap, unit, category, formatType = "11v11", canEdit, onDragStart, onDragOver, onDropOnSlot, onClear, onPositionMove, onSwap, onApplyPresetCoords }) {
  const [formations, setFormations] = useState([]);
  const [savingFormation, setSavingFormation] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("");
  const presetGroups = useMemo(() => getPresetGroups(category, unit), [category, unit]);

  const applyPreset = (presetName) => {
    if (!presetName) return;
    const coords = getPresetCoords(category, unit, presetName);
    if (!coords) return;
    onApplyPresetCoords(coords);
    toast.success(`Formación "${presetName}" aplicada`);
    setSelectedPreset(presetName);
  };

  const loadFormations = useCallback(() => {
    api.get(`/formations?category=${category}&unit=${unit}&format_type=${formatType}`)
      .then(r => setFormations(r.data)).catch(() => setFormations([]));
  }, [category, unit, formatType]);
  useEffect(() => { loadFormations(); }, [loadFormations]);

  const saveFormation = async () => {
    const name = prompt("Nombre de la formación (ej: I-Form, Shotgun, Spread):");
    if (!name?.trim()) return;
    setSavingFormation(true);
    try {
      await api.post("/formations", { name: name.trim(), category, unit, format_type: formatType });
      toast.success("Formación guardada");
      loadFormations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setSavingFormation(false); }
  };

  const applyFormation = async (id) => {
    await api.post(`/formations/${id}/apply`);
    toast.success("Formación aplicada");
    window.dispatchEvent(new Event("depth-reload"));
  };

  const deleteFormation = async (id) => {
    if (!confirm("¿Eliminar esta formación?")) return;
    await api.delete(`/formations/${id}`);
    loadFormations();
  };

  // Only show starters
  const starters = slots.filter(s => s.slot === "starter");
  const fieldRef = useRef(null);
  const [draggingId, setDraggingId] = useState(null);

  // ---- Plays (animation) state ----
  const [activePlay, setActivePlay] = useState(null);   // currently playing
  const [loop, setLoop] = useState(false);
  const [designing, setDesigning] = useState(false);
  const [designRoutes, setDesignRoutes] = useState({}); // {slot_id: [{x,y}, ...]}
  const [designSelectedSlotId, setDesignSelectedSlotId] = useState(null);

  const { positions: animPositions } = usePlayAnimation({
    play: activePlay,
    playing: !!activePlay,
    loop,
    onComplete: () => { if (!loop) setActivePlay(null); },
  });

  const startDesign = () => {
    if (activePlay) setActivePlay(null);
    setDesignRoutes({});
    setDesignSelectedSlotId(starters[0]?.id || null);
    setDesigning(true);
  };

  const cancelDesign = () => {
    setDesigning(false);
    setDesignRoutes({});
    setDesignSelectedSlotId(null);
  };

  const clearRouteForSlot = (slotId) => {
    setDesignRoutes(prev => {
      const next = { ...prev };
      delete next[slotId];
      return next;
    });
  };

  const handleFieldClickForDesign = (e) => {
    if (!designing || !fieldRef.current || !designSelectedSlotId) return;
    const rect = fieldRef.current.getBoundingClientRect();
    const x = Math.max(2, Math.min(98, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(2, Math.min(98, ((e.clientY - rect.top) / rect.height) * 100));
    setDesignRoutes(prev => {
      const current = prev[designSelectedSlotId] || [];
      // Cap at 5 waypoints to keep things sane
      if (current.length >= 5) {
        toast.warning("Máximo 5 waypoints por jugador");
        return prev;
      }
      return { ...prev, [designSelectedSlotId]: [...current, { x, y }] };
    });
  };

  const saveDesignedPlay = async () => {
    const entries = Object.entries(designRoutes).filter(([, w]) => w && w.length > 0);
    if (entries.length === 0) {
      toast.error("Añade al menos una ruta");
      return;
    }
    const name = prompt("Nombre de la play (ej: Power Right, Cover 2, Sweep):");
    if (!name?.trim()) return;
    const slotsMap = Object.fromEntries(starters.map(s => [s.id, s]));
    const routes = entries.map(([slot_id, waypoints]) => {
      const s = slotsMap[slot_id];
      return {
        slot_id,
        position: s.position,
        slot: s.slot,
        start: { x: s.x ?? 50, y: s.y ?? 50 },
        waypoints,
      };
    });
    try {
      await api.post("/plays", { name: name.trim(), category, unit, format_type: formatType, duration_ms: 3000, routes });
      toast.success(`Play "${name}" guardada`);
      cancelDesign();
      window.dispatchEvent(new Event("plays-reload"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    }
  };

  // Mouse-based drag on the field (separate from HTML5 DnD for pool→slot)
  const startFieldDrag = (e, slot) => {
    if (!canEdit || !slot.player_id) return;
    e.preventDefault();
    setDraggingId(slot.id);
  };

  const handleFieldMouseMove = (e) => {
    if (!draggingId || !fieldRef.current) return;
    const rect = fieldRef.current.getBoundingClientRect();
    const x = Math.max(2, Math.min(98, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(2, Math.min(98, ((e.clientY - rect.top) / rect.height) * 100));
    // Optimistic local move only — we save on release
    onPositionMove(draggingId, x, y);
  };

  const handleFieldMouseUp = () => {
    setDraggingId(null);
  };

  const isFlag = category !== "tackle";

  useEffect(() => {
    if (!draggingId) return;
    const move = (e) => handleFieldMouseMove(e);
    const up = () => handleFieldMouseUp();
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggingId]);

  // Determine field aspect: tackle = taller, flag = shorter
  const aspectClass = isFlag ? "aspect-[5/6]" : "aspect-[4/5]";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="overline">Formación · {UNIT_LABEL[unit]}</div>
        <div className="text-[10px] text-[#71717A] uppercase tracking-widest">
          {canEdit ? "Arrastra a los jugadores para reposicionar · Suelta jugadores del panel para asignar" : "Vista de cancha"}
        </div>
      </div>

      {/* Formations + Presets bar */}
      <div className="tactical-card p-3 space-y-3" data-testid="formations-bar">
        {/* Preset library dropdown */}
        {presetGroups.length > 0 && (
          <div className="flex items-center gap-3 flex-wrap">
            <span className="overline">Librería</span>
            <select
              value={selectedPreset}
              onChange={(e) => applyPreset(e.target.value)}
              disabled={!canEdit}
              data-testid="preset-select"
              className="input-tactical max-w-xs disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <option value="">— Selecciona formación oficial —</option>
              {presetGroups.map(g => (
                <optgroup key={g.group} label={g.group}>
                  {g.items.map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {selectedPreset && (
              <span className="text-[10px] text-[#34C759] uppercase tracking-widest">
                · {selectedPreset} aplicada
              </span>
            )}
          </div>
        )}

        {/* User-saved formations */}
        <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t border-[#27272A]">
          <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
            <span className="overline mr-1">Mis formaciones</span>
            {formations.length === 0 ? (
              <span className="text-[10px] text-[#71717A]">Sin formaciones guardadas</span>
            ) : (
              formations.map(f => (
                <div key={f.id} className="flex items-center border border-[#27272A] hover:border-[#007AFF]">
                  <button
                    onClick={() => canEdit && applyFormation(f.id)}
                    disabled={!canEdit}
                    className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-white hover:bg-[#1A1A1A] disabled:opacity-60"
                    data-testid={`apply-formation-${f.name}`}
                    title={canEdit ? "Aplicar formación" : "Solo coaches"}
                  >
                    {f.name}
                  </button>
                  {canEdit && (
                    <button onClick={() => deleteFormation(f.id)} className="px-2 py-1.5 text-[#71717A] hover:text-[#FF3B30] border-l border-[#27272A]" title="Eliminar">
                      <Trash2 size={10}/>
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
          {canEdit && (
            <button onClick={saveFormation} disabled={savingFormation} className="btn-ghost text-xs flex items-center gap-1" data-testid="save-formation-btn">
              <Plus size={12}/> Guardar actual
            </button>
          )}
        </div>
      </div>

      {/* Plays animadas */}
      <PlaysBar
        category={category}
        unit={unit}
        formatType={formatType}
        canEdit={canEdit}
        activePlayId={activePlay?.id}
        onPlay={(p) => { setDesigning(false); setActivePlay(p); }}
        onStopPlay={() => setActivePlay(null)}
        onStartDesign={startDesign}
        loop={loop}
        setLoop={setLoop}
        designing={designing}
      />

      {designing && (
        <PlayDesignerToolbar
          slots={slots}
          playerMap={playerMap}
          routes={designRoutes}
          selectedSlotId={designSelectedSlotId}
          setSelectedSlotId={setDesignSelectedSlotId}
          onClearRoute={clearRouteForSlot}
          onCancel={cancelDesign}
          onSave={saveDesignedPlay}
          category={category}
          unit={unit}
        />
      )}

      <div
        ref={fieldRef}
        className={`relative ${aspectClass} w-full max-w-[600px] mx-auto border-2 ${designing ? "border-[#FACC15]/70" : "border-white/20"} overflow-hidden select-none ${designing && designSelectedSlotId ? "cursor-crosshair" : ""}`}
        style={{
          background: "linear-gradient(180deg, #0a4d1e 0%, #0d5824 50%, #0a4d1e 100%)",
          backgroundImage: `
            repeating-linear-gradient(0deg, transparent, transparent 9.9%, rgba(255,255,255,0.18) 10%, rgba(255,255,255,0.18) calc(10% + 1px)),
            linear-gradient(180deg, #0a4d1e 0%, #0d5824 50%, #0a4d1e 100%)
          `,
        }}
        onClick={designing ? handleFieldClickForDesign : undefined}
        onDragOver={canEdit ? onDragOver : undefined}
        data-testid="formation-field"
      >
        {/* End zones */}
        <div className="absolute top-0 left-0 right-0 h-[8%] bg-[#FF3B30]/20 border-b border-white/30 flex items-center justify-center">
          <span className="font-display text-xs text-white/40 tracking-widest">END ZONE</span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[8%] bg-[#007AFF]/20 border-t border-white/30 flex items-center justify-center">
          <span className="font-display text-xs text-white/40 tracking-widest">END ZONE</span>
        </div>

        {/* 50-yard line */}
        <div className="absolute top-1/2 left-0 right-0 h-px bg-white/50"/>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-display text-3xl text-white/15">50</div>

        {/* Yard numbers */}
        {[10, 20, 30, 40].map(n => (
          <div key={`top-${n}`}>
            <div className="absolute left-3 font-display text-base text-white/30" style={{ top: `${8 + (50 - 8) * (n / 50)}%` }}>{n}</div>
            <div className="absolute right-3 font-display text-base text-white/30" style={{ top: `${8 + (50 - 8) * (n / 50)}%` }}>{n}</div>
          </div>
        ))}
        {[10, 20, 30, 40].map(n => (
          <div key={`bot-${n}`}>
            <div className="absolute left-3 font-display text-base text-white/30" style={{ bottom: `${8 + (50 - 8) * (n / 50)}%` }}>{n}</div>
            <div className="absolute right-3 font-display text-base text-white/30" style={{ bottom: `${8 + (50 - 8) * (n / 50)}%` }}>{n}</div>
          </div>
        ))}

        {/* Routes overlay: active play OR designing-in-progress */}
        {activePlay && (
          <RoutesOverlay routes={activePlay.routes} color="#FACC15" opacity={0.85}/>
        )}
        {designing && (
          <RoutesOverlay
            routes={Object.entries(designRoutes).map(([slot_id, wps]) => {
              const s = starters.find(x => x.id === slot_id);
              if (!s) return null;
              return {
                slot_id,
                start: { x: s.x ?? 50, y: s.y ?? 50 },
                waypoints: wps,
              };
            }).filter(Boolean)}
            color="#FACC15"
            opacity={0.9}
          />
        )}

        {/* Player markers */}
        {starters.map(s => {
          const animPos = activePlay && animPositions ? animPositions[s.id] : null;
          const x = animPos ? animPos.x : (s.x ?? 50);
          const y = animPos ? animPos.y : (s.y ?? 50);
          const player = s.player_id ? playerMap[s.player_id] : null;
          const status = player?.profile?.status || "active";
          const ringColor = status === "injured" ? "#FF9F0A" : status === "suspended" ? "#FF3B30" : (player ? "#FFFFFF" : "#71717A");
          const isOffense = (s.y ?? 50) > 50;
          const jerseyBg = isOffense ? "#007AFF" : "#FF3B30";
          const isDesignSelected = designing && designSelectedSlotId === s.id;
          const interactive = canEdit && !designing && !activePlay;
          return (
            <div
              key={s.id}
              onMouseDown={interactive ? (e) => startFieldDrag(e, s) : undefined}
              onClick={designing ? (e) => { e.stopPropagation(); setDesignSelectedSlotId(s.id); } : undefined}
              onDragOver={interactive ? onDragOver : undefined}
              onDrop={interactive ? (e) => onDropOnSlot(e, s.id) : undefined}
              data-testid={`field-marker-${s.position}`}
              className={`absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center ${interactive ? "cursor-grab active:cursor-grabbing" : designing ? "cursor-pointer" : ""} ${draggingId === s.id ? "z-30 scale-110" : isDesignSelected ? "z-30" : "z-10"}`}
              style={{
                left: `${x}%`,
                top: `${y}%`,
                transition: (draggingId === s.id || activePlay) ? "none" : "left 0.6s cubic-bezier(0.4, 0, 0.2, 1), top 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              title={player ? `${player.name} (${s.position})` : `${s.position} (vacío)`}
            >
              {/* Jersey circle */}
              <div
                className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 flex items-center justify-center font-display text-sm md:text-base font-bold text-white shadow-lg"
                style={{
                  background: player ? jerseyBg : "rgba(0,0,0,0.5)",
                  borderColor: isDesignSelected ? "#FACC15" : ringColor,
                  boxShadow: isDesignSelected ? "0 0 0 3px rgba(250,204,21,0.35)" : undefined,
                }}
              >
                {player?.profile?.jersey_number ?? "·"}
              </div>
              {/* Position label */}
              <div className="mt-0.5 px-1.5 py-0.5 bg-black/80 border border-white/30 font-display text-[10px] text-white">
                {s.position}
              </div>
              {/* Name */}
              {player && (
                <div className="mt-0.5 max-w-[80px] text-center text-[9px] text-white font-bold leading-tight truncate" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.9)" }}>
                  {player.name.split(" ")[0]}
                </div>
              )}
              {/* Status icon */}
              {player && status !== "active" && (
                <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center border border-white" style={{ background: ringColor }}>
                  {status === "injured" ? <AlertTriangle size={9} className="text-black"/> : <Ban size={9} className="text-white"/>}
                </div>
              )}
              {/* Moroso indicator (opposite corner) */}
              {player && player.delinquency?.is_delinquent && (
                <div
                  className="absolute -top-1 -left-1 w-4 h-4 rounded-full flex items-center justify-center border border-white bg-[#FF3B30]"
                  title={`Moroso · ${player.delinquency.months_overdue} cuotas`}
                  data-testid={`field-moroso-${player.user_id}`}
                >
                  <AlertOctagon size={9} className="text-white"/>
                </div>
              )}
            </div>
          );
        })}

        {/* Watermark */}
        <div className="absolute bottom-[10%] left-1/2 -translate-x-1/2 font-display text-2xl text-white/10 tracking-widest pointer-events-none">
          FERROVIARIOS
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 text-[10px] text-[#A1A1AA] uppercase tracking-widest flex-wrap">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#007AFF] border border-white"/> Ofensiva</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#FF3B30] border border-white"/> Defensiva / Especiales</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-black border-2 border-[#FF9F0A]"/> Lesionado</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-black border-2 border-[#FF3B30]"/> Suspendido</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#FF3B30] border border-white flex items-center justify-center"><AlertOctagon size={8} className="text-white"/></span> Moroso</span>
      </div>
    </div>
  );
}
